import { useEffect, useState, FC } from 'react';
import i18n from './i18n';
import TranslatorContext, { Options } from './TranslatorContext';
import Loader from '../Layout/Loader';

function conjugate(message: string, locale: string, isFemale = true) {
  let msg = message;
  if (['pl-PL', 'ru', 'de', 'cs'].includes(locale)) {
    while (msg.match(/\[(.*?)\|(.*?)\]/)) {
      msg = msg.replace(/\[(.*?)\|(.*?)\]/, (m, female, male) => {
        if (isFemale) return female;
        return male;
      });
    }
  }
  return msg;
}

interface TranslatorProviderProps {
  fallbackLanguage: string;
  children: React.ReactNode;
  translations: any;
}

export const TranslatorProvider: FC<TranslatorProviderProps> = ({
  fallbackLanguage,
  children,
  translations,
}) => {
  const [translationResourceLoaded, setTranslationResourceLoaded] = useState<boolean>(false);

  useEffect(() => {
    const serverEnv = import.meta.env.VITE_REACT_APP_ENV || 'prod';
    i18n.init({
      fallbackLng: serverEnv === 'dev' || serverEnv === 'stage' ? '' : fallbackLanguage, // language to use if translations are missing in the current language
    });

    const allowedLanguages = translations.map((translation: any) => translation.lang);
    translations.forEach((translation: any) => {
      i18n.addResourceBundle(translation.lang, 'translations', translation.translation, true, true);
    });

    // set language if language detector detect language outside of allowed languages
    if (!allowedLanguages.includes(i18n.language)) {
      if (i18n.language) {
        // for sweedish language
        if (i18n.language.startsWith('sv')) {
          i18n.changeLanguage('sv-SE');
        } else {
          i18n.changeLanguage(fallbackLanguage);
        }
      } else {
        // for sweedish language from navigaror
        if (navigator.language.startsWith('sv')) {
          i18n.changeLanguage('sv-SE');
        } else {
          i18n.changeLanguage(fallbackLanguage);
        }
      }
    }

    // hack to wait for the next tick to set the state (waiting for loading translations)
    setTimeout(() => {
      setTranslationResourceLoaded(true);
    }, 0);
  }, [fallbackLanguage, translations]);

  const t = (message: string, optionsProps?: Options): string => {
    const options = {
      ...optionsProps,
      sex: optionsProps?.sex || window.localStorage.getItem('sex') || 'female',
      conjugation: optionsProps?.conjugation === false ? false : true,
    };
    /*
            required params: message (string)
            optional params:
              from documentation 
              https://www.i18next.com/translation-function/

            +
                sex
                    - force sex for conjugator (if undefined, used from store)
                conjugate
                    - default: true, could disable conjugator mechanism
                    for more complicated messages (like links with regexp etc.)
            example:
                this.props.t('xoxo', {sex: 'male'})
        */

    if (message === undefined) {
      const error = new Error('Translation function called with undefined message');

      console.warn(error);
      return '';
    }
    const isFemale = options.sex === 'female';

    if (!options.count) {
      if (!i18n.exists(message)) {
        const error = new Error(`Missing translation string for ${message}`);

        console.warn(error);
      }
    } else {
      const missingKeys = i18n.services.pluralResolver
        .getPluralFormsOfKey(i18n.language, message)
        .filter((key: any) => !i18n.exists(key));

      if (missingKeys.length !== 0) {
        const error = new Error(`Missing translation strings for ${missingKeys.join(', ')}`);

        console.warn(error);
      }
    }

    const translatedMessage = i18n.t(message, options);

    if (options.conjugation) {
      return conjugate(translatedMessage, i18n.language, isFemale);
    }
    return translatedMessage;
  };

  const changeLanguage = async (lang: string) => {
    i18n.changeLanguage(lang);
    window.location.reload();
  };

  if (translationResourceLoaded) {
    return (
      <TranslatorContext.Provider value={{ t, language: i18n.language, changeLanguage }}>
        {children}
      </TranslatorContext.Provider>
    );
  }
  return (
    <div className="full-height">
      <Loader />
    </div>
  );
};

export default TranslatorProvider;
